<template>
  <div id="ruumi-app" class="min-h-screen overflow-x-hidden bg-gradient-to-br from-amber-50 to-orange-50 md:h-screen md:overflow-hidden">
    <!-- Global Toast Notifications -->
    <Toast ref="toast" :position="windowWidth < 768 ? 'top-center' : 'top-right'" />
    
    <!-- Global Confirmation Dialog -->
    <ConfirmDialog />

    <!-- Navigation Drawer (for logged-in users) -->
    <Drawer 
      v-if="shouldShowNav" 
      v-model:visible="visible" 
      :header="t('common.Menu')"
      class="ruumi-drawer"
      :pt="{
        root: { class: '!w-80 !bg-white' },
        mask: { class: '!bg-black/40' },
        header: { class: '!bg-gradient-to-r !from-amber-600 !to-amber-700 !text-white !p-0' },
        content: { class: '!bg-white !p-0 !flex !flex-col !h-full' },
        pcCloseButton: {
          root: { class: '!bg-white/20 !text-white hover:!bg-white/30 !border-0 !w-8 !h-8' }
        }
      }"
    >
      <template #header>
        <div class="flex items-center justify-center p-4 bg-gradient-to-r from-amber-600 to-amber-700">
          <img src="@/assets/logo.svg" alt="Ruumi" class="h-12 bg-white rounded-lg px-2 py-1" />
        </div>
      </template>

      <!-- Navigation Menu -->
      <div class="py-2 bg-white flex-1">
        <div
          v-for="item in menuItems"
          :key="item.label"
          @click="item.command"
          class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-amber-50 transition-colors"
          :class="{ 'bg-amber-100 border-l-4 border-amber-500': route.path === item.command.toString().match(/\/[^']+/)?.[0] }"
        >
          <i :class="item.icon" class="text-amber-600"></i>
          <span class="text-gray-800 font-medium">{{ item.label }}</span>
        </div>
      </div>

      <!-- Bottom Actions (moved to UserProfilePopover) -->
      <template #footer>
        <div class="border-t border-gray-200 p-4 space-y-2 bg-white"></div>
      </template>
    </Drawer>

    <!-- Main Content Area -->
    <div class="flex flex-col min-h-screen md:h-screen">
      <!-- Top Bar (always visible) -->
      <div class="bg-white border-b border-amber-200 ruumi-shadow-sm sticky top-0 z-[60]">
        <!-- Main Navbar -->
        <div class="mx-auto px-4 py-3 flex items-center justify-between">
          <!-- Menu Toggle (Administrator and Receptionist only, when logged in) -->
          <Button
            v-if="shouldShowNav && canAccessMenu"
            icon="pi pi-bars"
            @click="visible = !visible"
            class="mr-2"
            text
            rounded
          />

          <!-- Logo - Directs to home (public) or dashboard (logged in) -->
          <router-link 
            :to="shouldShowNav ? '/app' : '/'" 
            class="flex items-center px-3 py-1 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
            :title="shouldShowNav ? t('breadcrumb.Dashboard') : t('public.WelcomeToRuumi')"
          >
            <img src="@/assets/logo.svg" alt="Ruumi" class="h-10" />
          </router-link>

          <!-- Breadcrumb (Desktop only, for logged-in users) -->
          <Breadcrumb
            v-if="shouldShowNav && route.meta.breadcrumb"
            :model="route.meta.breadcrumb as any"
            class="hidden md:flex flex-1 ml-4"
          >
            <template #item="{ item }">
              <router-link
                v-if="item.to"
                :to="item.to"
                class="text-amber-600 hover:text-amber-700"
              >
                {{ item.translate !== false ? t(`breadcrumb.${item.label}`) : item.label }}
              </router-link>
              <span v-else class="text-gray-600">
                {{ item.translate !== false ? t(`breadcrumb.${item.label}`) : item.label }}
              </span>
            </template>
          </Breadcrumb>

          <!-- Right Side Actions -->
          <div class="flex items-center gap-2 ml-auto">
            <template v-if="!shouldShowNav">
              <LangPicker class="w-24" />
              <!-- Login Button (when NOT logged in) -->
              <Button
                icon="pi pi-sign-in"
                :label="t('authentication.Login')"
                @click="router.push('/login')"
                outlined
                class="!border-amber-500 !text-amber-600 hover:!bg-amber-50"
              />
            </template>

            <!-- User Profile (when logged in) -->
            <template v-if="shouldShowNav && currentUser">
              <LangPicker />
              <UserProfilePopover />
            </template>
          </div>
        </div>

        <!-- Breadcrumb Bar (Mobile only, for logged-in users) -->
        <div v-if="shouldShowNav && route.meta.breadcrumb" class="md:hidden px-4 py-2 bg-amber-50 border-t border-amber-100">
          <Breadcrumb
            :model="route.meta.breadcrumb as any"
            class="text-sm"
          >
            <template #item="{ item }">
              <router-link
                v-if="item.to"
                :to="item.to"
                class="text-amber-600 hover:text-amber-700 text-sm"
              >
                {{ item.translate !== false ? t(`breadcrumb.${item.label}`) : item.label }}
              </router-link>
              <span v-else class="text-gray-600 text-sm">
                {{ item.translate !== false ? t(`breadcrumb.${item.label}`) : item.label }}
              </span>
            </template>
          </Breadcrumb>
        </div>
      </div>

      <!-- Router View - Main Application Content -->
      <div
        class="flex-1 min-h-0"
        :class="shouldScrollInBookingCalendar ? 'overflow-hidden' : 'overflow-y-auto'"
      >
        <RouterView />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useToastStore } from '@/stores/toastStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { PrivilegesConstants } from '@/constants/privilegesConstants';
import { RoleConstants } from '@/constants/roleConstants';
import LangPicker from './components/common/LangPicker.vue';
import UserProfilePopover from './components/common/UserProfilePopover.vue';
import { useLocaleSettings } from '@/composables/localeSettings';

const { t, locale } = useI18n({ useScope: 'global' });
const route = useRoute();
const router = useRouter();
const authStore = useAuthenticationStore();
const toastStore = useToastStore();
const notificationStore = useNotificationStore();
const localeHelper = useLocaleSettings();

const visible = ref(false);
const toast = ref();
const windowWidth = ref(window.innerWidth);

// Computed
const isLoggedIn = computed(() => authStore.isAuthenticated);
const currentUser = computed(() => authStore.currentUser);
const shouldShowNav = computed(() => isLoggedIn.value && route.path !== '/login');

// Check if user can access menu (Administrator or Receptionist only)
const canAccessMenu = computed(() => {
  const roleName = authStore.currentUser?.roleName?.toUpperCase();
  return roleName === RoleConstants.Administrator || roleName === RoleConstants.Receptionist;
});

// Responsive drawer behavior
const isMobile = computed(() => windowWidth.value < 1024);

// Role check
const isTherapist = computed(() => authStore.currentUser?.roleName?.toUpperCase() === RoleConstants.Therapist);

const shouldScrollInBookingCalendar = computed(() => {
  return route.path === '/' || route.path.startsWith('/bookings');
});

// Menu items with privilege filtering
const menuItems = computed(() => {
  const items = [
    {
      label: t('menu.Dashboard'),
      icon: 'pi pi-home',
      command: () => navigate('/'),
      visible: true
    },
    {
      label: t('menu.Bookings'),
      icon: 'pi pi-calendar',
      command: () => navigate('/bookings'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewBookings)
    },
    {
      label: t('menu.Waitlist'),
      icon: 'pi pi-clock',
      command: () => navigate('/waitlist'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewWaitlist)
    },
    {
      label: t('menu.Clients'),
      icon: 'pi pi-users',
      command: () => navigate('/clients'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewClients)
    },
    {
      label: t('menu.Employees'),
      icon: 'pi pi-id-card',
      command: () => navigate('/employees'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewEmployees)
    },
    {
      label: t('menu.Suggestions'),
      icon: 'pi pi-lightbulb',
      command: () => navigate('/suggestions'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewSuggestions)
    },
    {
      label: t('menu.Notifications'),
      icon: 'pi pi-bell',
      command: () => navigate('/notifications'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewAllNotifications)
    },
    {
      label: t('menu.Reports'),
      icon: 'pi pi-chart-bar',
      command: () => navigate('/reports'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewReports)
    },
    {
      label: t('menu.Rooms'),
      icon: 'pi pi-home',
      command: () => navigate('/rooms'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewRooms)
    },
    {
      label: t('breadcrumb.ServicePasses'),
      icon: 'pi pi-ticket',
      command: () => navigate('/service-passes'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ViewServicePasses)
    },
    {
      label: t('menu.Settings'),
      icon: 'pi pi-cog',
      command: () => navigate('/settings'),
      visible: !isTherapist.value && authStore.hasPrivilege(PrivilegesConstants.ManageSystemSettings)
    }
  ];

  return items.filter(item => item.visible);
});

// Methods
const navigate = (path: string): void => {
  router.push(path);
  if (isMobile.value) {
    visible.value = false; // Auto-close drawer on mobile after navigation
  }
};

const handleResize = (): void => {
  windowWidth.value = window.innerWidth;
};

// Watch for authentication status and connect/disconnect SignalR
watch(() => authStore.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated) {
    await notificationStore.connectSignalR();
    await notificationStore.loadUnreadNotifications();
  } else {
    await notificationStore.disconnectSignalR();
    notificationStore.clearNotifications();
  }
});

// Watch for window resize
onMounted(async () => {
  localeHelper.InitLocale(); // Initialize locale settings from localStorage
  toastStore.setToastRef(toast.value);
  window.addEventListener('resize', handleResize);
  
  // Connect to SignalR if already authenticated
  if (authStore.isAuthenticated) {
    await notificationStore.connectSignalR();
    await notificationStore.loadUnreadNotifications();
  }
});

// Cleanup
onBeforeUnmount(async () => {
  await notificationStore.disconnectSignalR();
  window.removeEventListener('resize', handleResize);
});


// Watch for window resize
onMounted(() => {
  localeHelper.InitLocale(); // Initialize locale settings from localStorage
  toastStore.setToastRef(toast.value);
  window.addEventListener('resize', handleResize);
});

// Cleanup
watch(() => route.path, () => {
  if (isMobile.value && visible.value) {
    visible.value = false;
  }
});
</script>

<style>
/* Global Ruumi Application Styles */
#ruumi-app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Force drawer to be white background */
.p-drawer,
.p-drawer-content,
.ruumi-drawer .p-drawer-content {
  background-color: #ffffff !important;
}

/* Force drawer close button to be amber */
.p-drawer .p-drawer-close-button,
.p-drawer .p-drawer-header-close,
.p-drawer-header .p-button {
  color: #ffffff !important;
}

.p-drawer .p-drawer-close-button:hover,
.p-drawer .p-drawer-header-close:hover,
.p-drawer-header .p-button:hover {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #ffffff !important;
}

/* Force outlined buttons in drawer to be amber with white background */
.p-drawer .p-button.p-button-outlined,
.ruumi-drawer .p-button.p-button-outlined {
  border: 1px solid #F59E0B !important;
  color: #F59E0B !important;
  background-color: #ffffff !important;
}

.p-drawer .p-button.p-button-outlined:enabled:hover,
.ruumi-drawer .p-button.p-button-outlined:enabled:hover {
  background-color: #FFFBEB !important;
  border-color: #D97706 !important;
  color: #D97706 !important;
}

.p-drawer .p-button.p-button-outlined .p-button-label,
.ruumi-drawer .p-button.p-button-outlined .p-button-label {
  color: #F59E0B !important;
}

.p-drawer .p-button.p-button-outlined:enabled:hover .p-button-label,
.ruumi-drawer .p-button.p-button-outlined:enabled:hover .p-button-label {
  color: #D97706 !important;
}

.p-drawer .p-button.p-button-outlined .p-button-icon,
.ruumi-drawer .p-button.p-button-outlined .p-button-icon {
  color: #F59E0B !important;
}

.p-drawer .p-button.p-button-outlined:enabled:hover .p-button-icon,
.ruumi-drawer .p-button.p-button-outlined:enabled:hover .p-button-icon {
  color: #D97706 !important;
}

/* Ensure drawer works well on mobile */
@media (max-width: 1024px) {
  .p-drawer {
    width: 80vw !important;
    max-width: 320px !important;
  }
}
</style>
